/*
 * Griffe & Cime — tri et pages des avis clients (section gc-avis-clients). Vanilla, chargé en defer.
 *
 * - Le serveur rend la page 1 dans l'ordre par défaut : avis avec photos d'abord, puis du plus
 *   récent au plus ancien.
 * - Tri : date (récents, anciens), note (haute, basse), case « photos en premier » (cochée).
 * - Pages : flèches et numéros (1 … 4 5 6 … 69), sans quitter ni recharger la page ; au changement
 *   de page, retour en haut de la liste et focus sur la liste.
 * - Au premier tri ou au premier changement de page, toutes les données sont chargées depuis
 *   [data-source]?view=donnees-avis (250 avis par appel, champ « suivant » pour la suite),
 *   puis les cartes sont reconstruites à partir du <template data-gc-avis-modele>.
 * - Mode « extrait » : avis qui ont un texte ou des photos. Mode « complet » : tous les avis.
 * Aucun texte affiché n'est écrit ici : tout vient des attributs data-* rendus par la section.
 */
(() => {
  'use strict';

  const formatDate = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
  });
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqMouvement = window.matchMedia('(prefers-reduced-motion: reduce)');

  const remplacer = (modele, valeurs) => Object.entries(valeurs)
    .reduce((texte, [cle, valeur]) => texte.split(`[${cle}]`).join(String(valeur)), modele || '');

  const COMPARER = {
    recent: (a, b) => b.d.localeCompare(a.d) || a.h.localeCompare(b.h),
    ancien: (a, b) => a.d.localeCompare(b.d) || b.h.localeCompare(a.h),
    'note-haute': (a, b) => b.n - a.n || COMPARER.recent(a, b),
    'note-basse': (a, b) => a.n - b.n || COMPARER.recent(a, b)
  };

  // Numéros affichés : première, dernière, courante et ses voisines ; null = points de suspension.
  function numeros(courante, derniere, voisines) {
    const gardees = new Set([1, derniere]);
    for (let n = courante - voisines; n <= courante + voisines; n += 1) {
      if (n >= 1 && n <= derniere) gardees.add(n);
    }
    const triees = [...gardees].sort((a, b) => a - b);
    const sortie = [];
    triees.forEach((n, i) => {
      const ecart = i ? n - triees[i - 1] : 1;
      if (ecart === 2) sortie.push(n - 1);
      else if (ecart > 2) sortie.push(null);
      sortie.push(n);
    });
    return sortie;
  }

  async function chargerTout(source) {
    const avis = [];
    let url = `${source}${source.includes('?') ? '&' : '?'}view=donnees-avis`;
    for (let i = 0; url && i < 20; i += 1) {
      const reponse = await fetch(url, { credentials: 'same-origin' });
      if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
      const donnees = JSON.parse(await reponse.text());
      avis.push(...donnees.avis);
      url = donnees.suivant || null;
      if (url && !/[?&]view=donnees-avis/.test(url)) url += `${url.includes('?') ? '&' : '?'}view=donnees-avis`;
    }
    return avis;
  }

  function initialiser(racine) {
    const liste = racine.querySelector('[data-gc-avis-liste]');
    const modele = racine.querySelector('[data-gc-avis-modele]');
    const outils = racine.querySelector('[data-gc-avis-outils]');
    const choixTri = racine.querySelector('[data-gc-avis-tri]');
    const casePhotos = racine.querySelector('[data-gc-avis-photos]');
    const pagination = racine.querySelector('[data-gc-avis-pagination]');
    const pages = racine.querySelector('[data-gc-avis-pages]');
    const fleches = Array.from(racine.querySelectorAll('[data-gc-avis-pas]'));
    const annonce = racine.querySelector('[data-gc-avis-annonce]');
    const erreur = racine.querySelector('[data-gc-avis-erreur]');
    const source = racine.dataset.source;
    const complet = racine.dataset.mode === 'complet';
    const parPage = Number(racine.dataset.parPage) || 6;
    const anonymes = (racine.dataset.anonymes || '').split('|');
    if (!liste || !modele || !source) return;

    let donnees = null;
    let chargement = null;
    let ordre = null;
    let page = 1;
    let nbPages = Math.max(1, Math.ceil((Number(racine.dataset.total) || 0) / parPage));

    if (outils) outils.hidden = false;

    const charger = () => {
      if (!chargement) {
        chargement = chargerTout(source).then((avis) => {
          donnees = complet ? avis : avis.filter((a) => a.t.trim() || a.p.length);
          return donnees;
        });
        chargement.catch(() => { chargement = null; });
      }
      return chargement;
    };

    const ordonner = () => {
      const tries = [...donnees].sort(COMPARER[choixTri.value] || COMPARER.recent);
      if (!casePhotos.checked) return tries;
      return [...tries.filter((a) => a.p.length), ...tries.filter((a) => !a.p.length)];
    };

    function carte(avis) {
      const noeud = modele.content.firstElementChild.cloneNode(true);
      const champ = (nom) => noeud.querySelector(`[data-champ="${nom}"]`);
      const etoiles = noeud.querySelector('.gc-etoiles');
      etoiles.setAttribute('aria-label', remplacer(etoiles.getAttribute('aria-label'), { n: avis.n }));
      noeud.querySelector('.gc-etoiles__plein').style.width = `${Math.max(0, Math.min(5, avis.n)) * 20}%`;

      const auteur = (avis.a || '').trim();
      if (auteur && !anonymes.includes(auteur)) champ('auteur').textContent = auteur;
      else champ('auteur').remove();

      const date = champ('date');
      if (avis.d) {
        date.dateTime = avis.d;
        date.textContent = formatDate.format(new Date(`${avis.d}T00:00:00Z`));
      } else date.remove();

      const ecrireLignes = (element, texte) => {
        texte.split('\n').forEach((ligne, i) => {
          if (i) element.append(document.createElement('br'));
          element.append(ligne);
        });
      };
      if (avis.t.trim()) ecrireLignes(champ('texte'), avis.t.trim());
      else champ('texte').remove();

      const photos = champ('photos');
      if (avis.p.length) {
        avis.p.forEach((photo, i) => {
          const element = document.createElement('li');
          const lien = document.createElement('a');
          lien.href = photo.g;
          lien.className = 'gc-avis-carte__photo';
          lien.dataset.gcAgrandir = '';
          lien.dataset.gcGroupe = avis.h;
          lien.dataset.alt = remplacer(racine.dataset.photoAlt, { n: i + 1 });
          const image = document.createElement('img');
          image.src = photo.m;
          image.width = 150;
          image.height = 150;
          image.loading = 'lazy';
          image.alt = '';
          const libelle = document.createElement('span');
          libelle.className = 'gc-visuellement-cache';
          libelle.textContent = remplacer(racine.dataset.photoOuvrir, { n: i + 1 });
          lien.append(image, libelle);
          element.append(lien);
          photos.append(element);
        });
      } else photos.remove();

      if (avis.r.trim()) ecrireLignes(champ('reponse'), avis.r.trim());
      else champ('reponse-bloc').remove();
      return noeud;
    }

    function construirePages() {
      if (!pagination) return;
      pagination.hidden = nbPages < 2;
      fleches.forEach((fleche) => {
        const cible = page + Number(fleche.dataset.gcAvisPas);
        fleche.disabled = cible < 1 || cible > nbPages;
      });
      // Téléphone : une seule voisine aux extrémités, aucune au milieu (la ligne doit tenir en 320 px).
      const voisines = !mqMobile.matches || page === 1 || page === nbPages ? 1 : 0;
      pages.replaceChildren(...numeros(page, nbPages, voisines).map((n) => {
        const element = document.createElement('li');
        if (n === null) {
          element.className = 'gc-avis-pagination__ellipse';
          element.setAttribute('aria-hidden', 'true');
          element.textContent = '…';
          return element;
        }
        const bouton = document.createElement('button');
        bouton.type = 'button';
        bouton.className = 'gc-avis-pagination__page';
        bouton.dataset.gcAvisPage = String(n);
        bouton.textContent = String(n);
        bouton.setAttribute('aria-label', remplacer(pagination.dataset.page, { n }));
        if (n === page) bouton.setAttribute('aria-current', 'page');
        element.append(bouton);
        return element;
      }));
    }

    function afficher() {
      nbPages = Math.max(1, Math.ceil(ordre.length / parPage));
      page = Math.min(Math.max(1, page), nbPages);
      liste.replaceChildren(...ordre.slice((page - 1) * parPage, page * parPage).map(carte));
      construirePages();
      if (annonce) annonce.textContent = remplacer(racine.dataset.statutPage, { n: page, t: nbPages });
    }

    async function appliquer({ nouvellePage = 1, retrier = false, remonter = false } = {}) {
      erreur.hidden = true;
      liste.setAttribute('aria-busy', 'true');
      racine.classList.add('gc-avis-en-chargement');
      try {
        await charger();
        if (retrier || !ordre) ordre = ordonner();
        page = nouvellePage;
        afficher();
        if (remonter) {
          liste.scrollIntoView({ behavior: mqMouvement.matches ? 'auto' : 'smooth', block: 'start' });
          liste.focus({ preventScroll: true });
        }
      } catch (e) {
        erreur.hidden = false;
      } finally {
        liste.removeAttribute('aria-busy');
        racine.classList.remove('gc-avis-en-chargement');
      }
    }

    construirePages();
    choixTri.addEventListener('change', () => appliquer({ retrier: true }));
    casePhotos.addEventListener('change', () => appliquer({ retrier: true }));
    fleches.forEach((fleche) => {
      fleche.addEventListener('click', () => appliquer({ nouvellePage: page + Number(fleche.dataset.gcAvisPas), remonter: true }));
    });
    pages.addEventListener('click', (evenement) => {
      const bouton = evenement.target.closest('[data-gc-avis-page]');
      if (bouton && Number(bouton.dataset.gcAvisPage) !== page) {
        appliquer({ nouvellePage: Number(bouton.dataset.gcAvisPage), remonter: true });
      }
    });
    mqMobile.addEventListener('change', construirePages);
  }

  document.querySelectorAll('[data-gc-avis]').forEach(initialiser);
})();
